import React, { useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Card,
  useMediaQuery,
  useTheme,
  TextField,
  Pagination,
  InputAdornment,
  Snackbar,
  Tooltip,
} from '@mui/material';
import { Add, ArrowBack, Delete, MailOutline, Person, Refresh, Search as SearchIcon } from '@mui/icons-material';
import apiClient from '../lib/axios';

type UserStatus = 'ACTIVE' | 'INVITED' | 'INVITATION_EXPIRED' | 'INACTIVE';

interface UserListItem {
  id: string;
  email: string;
  role: string;
  isActive: boolean;
  status: UserStatus;
  invitationExpiresAt?: string;
  createdAt: string;
  lastActive?: string;
  searchCount30d?: number;
  doctorProfile?: {
    id: string;
    firstName: string;
    lastName: string;
    licenseNumber: string;
    specialtyName?: string;
  };
}

interface PaginatedUsersResponse {
  data: UserListItem[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

function getActivityStatus(lastActive?: string): { label: string; color: 'success' | 'warning' | 'default' } {
  if (!lastActive) return { label: 'Inactivo', color: 'default' };
  const daysSince = (Date.now() - new Date(lastActive).getTime()) / (1000 * 60 * 60 * 24);
  if (daysSince <= 7) return { label: 'Activo', color: 'success' };
  if (daysSince <= 30) return { label: 'Reciente', color: 'warning' };
  return { label: 'Inactivo', color: 'default' };
}

const NAVY = '#0D1B35';
const BLUE = '#0066CC';

const roleColors: Record<string, 'default' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning'> = {
  SUPER_ADMIN: 'error',
  DOCTOR: 'primary',
  CUSTOMER: 'info',
  PHARMACY_MANAGER: 'secondary',
  PHARMACY_EMPLOYEE: 'default',
};

const roleLabels: Record<string, string> = {
  SUPER_ADMIN: 'Super Admin',
  DOCTOR: 'Doctor',
  CUSTOMER: 'Cliente',
  PHARMACY_MANAGER: 'Encargado Farmacia',
  PHARMACY_EMPLOYEE: 'Empleado Farmacia',
};

type StatusChipMeta = {
  label: string;
  color: 'success' | 'warning' | 'error' | 'default';
  ariaLabel: string;
  hint?: string;
};

function getStatusMeta(user: UserListItem): StatusChipMeta {
  switch (user.status) {
    case 'ACTIVE':
      return { label: 'Activo', color: 'success', ariaLabel: 'Usuario activo' };
    case 'INVITED': {
      const expires = user.invitationExpiresAt ? new Date(user.invitationExpiresAt) : null;
      const hoursLeft = expires ? Math.max(0, Math.round((expires.getTime() - Date.now()) / 3_600_000)) : null;
      return {
        label: 'Invitado',
        color: 'warning',
        ariaLabel: 'Invitación enviada, pendiente de onboarding',
        hint: hoursLeft != null ? `Expira en ~${hoursLeft}h` : undefined,
      };
    }
    case 'INVITATION_EXPIRED':
      return {
        label: 'Invitación expirada',
        color: 'error',
        ariaLabel: 'La invitación expiró sin ser aceptada',
      };
    case 'INACTIVE':
    default:
      return { label: 'Inactivo', color: 'default', ariaLabel: 'Usuario desactivado' };
  }
}

function getDoctorSecondaryLine(user: UserListItem): string | null {
  if (user.status === 'INVITED') return 'Pendiente de onboarding';
  if (user.status === 'INVITATION_EXPIRED') return 'Invitación expirada';
  return null;
}

// Pending users haven't completed onboarding — editing them would be meaningless
// since the doctor fills in their own profile. They can still be deleted (to
// cancel the invitation), but the row itself is not interactive for editing.
const isPendingOnboarding = (user: UserListItem): boolean =>
  user.status === 'INVITED' || user.status === 'INVITATION_EXPIRED';

const StatusChip: React.FC<{ user: UserListItem; size?: 'small' | 'medium' }> = ({ user, size = 'small' }) => {
  const meta = getStatusMeta(user);
  const showIcon = user.status === 'INVITED';
  return (
    <Chip
      size={size}
      color={meta.color}
      label={meta.label}
      icon={showIcon ? <MailOutline sx={{ fontSize: '14px !important' }} /> : undefined}
      aria-label={meta.hint ? `${meta.ariaLabel} — ${meta.hint}` : meta.ariaLabel}
      sx={
        user.status === 'INVITED'
          ? { '& .MuiChip-icon': { ml: 0.5, color: 'warning.dark' } }
          : undefined
      }
    />
  );
};

const LIMIT = 20;

export const UserList: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [filterRole, setFilterRole] = useState<string>('all');
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<UserListItem | null>(null);
  const [resendingUserId, setResendingUserId] = useState<string | null>(null);
  const [snackbar, setSnackbar] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const { data: paginatedResult, isLoading, error } = useQuery({
    queryKey: ['admin-users', search, page, filterRole],
    queryFn: async () => {
      const params: Record<string, string | number> = { page, limit: LIMIT };
      if (search) params.search = search;
      if (filterRole !== 'all') params.role = filterRole;
      const { data } = await apiClient.get<PaginatedUsersResponse>('/admin/users', { params });
      return data;
    },
  });

  // Separate lightweight query for role counts in tabs (uses cached admin-stats)
  const { data: stats } = useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const { data } = await apiClient.get('/admin/stats');
      return data;
    },
    staleTime: 60_000,
  });

  const deleteUserMutation = useMutation({
    mutationFn: async (userId: string) => {
      await apiClient.delete(`/admin/users/${userId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      queryClient.invalidateQueries({ queryKey: ['admin-stats'] });
      setDeleteDialogOpen(false);
      setUserToDelete(null);
    },
  });

  const resendInvitationMutation = useMutation({
    mutationFn: async (userId: string) => {
      setResendingUserId(userId);
      await apiClient.post(`/admin/users/${userId}/resend-invitation`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setSnackbar({ message: 'Invitación reenviada correctamente', severity: 'success' });
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message ?? 'No se pudo reenviar la invitación';
      setSnackbar({ message: msg, severity: 'error' });
    },
    onSettled: () => {
      setResendingUserId(null);
    },
  });

  const handleResendInvitation = (e: React.MouseEvent, user: UserListItem) => {
    e.stopPropagation();
    resendInvitationMutation.mutate(user.id);
  };

  const handleSearchChange = useCallback((value: string) => {
    setSearchInput(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setSearch(value);
      setPage(1);
    }, 400);
  }, []);

  const handleRoleChange = useCallback((_: React.SyntheticEvent, value: string) => {
    setFilterRole(value);
    setPage(1);
  }, []);

  const handleDeleteClick = (user: UserListItem) => {
    setUserToDelete(user);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = () => {
    if (userToDelete) deleteUserMutation.mutate(userToDelete.id);
  };

  const getUserDisplayName = (user: UserListItem): string => {
    // Invited doctors have an empty doctor profile — firstName/lastName are null
    // until onboarding completes. Fall back to the email so we never render
    // "Dr. null null".
    if (user.doctorProfile?.firstName && user.doctorProfile?.lastName) {
      return `Dr. ${user.doctorProfile.firstName} ${user.doctorProfile.lastName}`;
    }
    return user.email;
  };

  const hasFullDoctorName = (user: UserListItem): boolean =>
    !!user.doctorProfile?.firstName && !!user.doctorProfile?.lastName;

  const users = paginatedResult?.data ?? [];
  const total = paginatedResult?.total ?? 0;
  const totalPages = paginatedResult?.totalPages ?? 1;

  const roleCounts = {
    all: stats?.totalUsers ?? 0,
    SUPER_ADMIN: stats?.usersByRole?.SUPER_ADMIN ?? 0,
    DOCTOR: stats?.usersByRole?.DOCTOR ?? 0,
    CUSTOMER: stats?.usersByRole?.CUSTOMER ?? 0,
    PHARMACY_MANAGER: stats?.usersByRole?.PHARMACY_MANAGER ?? 0,
    PHARMACY_EMPLOYEE: stats?.usersByRole?.PHARMACY_EMPLOYEE ?? 0,
  };

  return (
    <Box>
      {/* Hero */}
      <Box
        sx={{
          borderRadius: 4,
          bgcolor: NAVY,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)',
          backgroundSize: '20px 20px',
          p: { xs: '28px 24px', sm: '40px 44px' },
          mb: 3,
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        <Box sx={{ position: 'absolute', width: 260, height: 260, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.12)', top: -80, right: -60, pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 140, height: 140, borderRadius: '50%', bgcolor: 'rgba(0,102,204,0.08)', bottom: -50, left: '35%', pointerEvents: 'none' }} />
        <Box sx={{ position: 'absolute', width: 80, height: 80, borderRadius: '50%', bgcolor: 'rgba(255,255,255,0.03)', top: '20%', right: '20%', pointerEvents: 'none' }} />
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 2 }}>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.75 }}>
              <IconButton size="small" onClick={() => navigate('/admin/dashboard')} sx={{ color: 'rgba(255,255,255,0.5)', p: 0.5 }}>
                <ArrowBack fontSize="small" />
              </IconButton>
              <Typography sx={{ color: 'rgba(255,255,255,0.5)', fontSize: 11, fontWeight: 700, letterSpacing: '2px', textTransform: 'uppercase' }}>
                USUARIOS
              </Typography>
            </Box>
            <Typography variant="h2" sx={{ color: '#fff', mb: 0.5 }}>
              Gestión de Usuarios
            </Typography>
            <Typography sx={{ color: 'rgba(255,255,255,0.55)', fontSize: { xs: 13, sm: 14 } }}>
              {total > 0 ? `${total} usuario${total !== 1 ? 's' : ''}${search ? ` encontrados` : ''}` : 'Cargando...'}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => navigate('/admin/users/create')}
            sx={{ bgcolor: BLUE, flexShrink: 0, mt: { xs: 0, sm: 1 }, '&:hover': { bgcolor: '#0052a3' } }}
          >
            {isMobile ? 'Crear' : 'Crear Usuario'}
          </Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>Error al cargar usuarios. Intenta de nuevo.</Alert>}

      {/* Search */}
      <TextField
        size="small"
        placeholder="Buscar por email, nombre, licencia o farmacia..."
        value={searchInput}
        onChange={(e) => handleSearchChange(e.target.value)}
        sx={{ mb: 2, width: { xs: '100%', sm: 400 } }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
            </InputAdornment>
          ),
        }}
      />

      {isLoading ? (
        <Box display="flex" justifyContent="center" p={4}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <Tabs
            value={filterRole}
            onChange={handleRoleChange}
            sx={{ mb: 2 }}
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab label={`Todos (${roleCounts.all})`} value="all" />
            <Tab label={`Admins (${roleCounts.SUPER_ADMIN})`} value="SUPER_ADMIN" />
            <Tab label={`Doctores (${roleCounts.DOCTOR})`} value="DOCTOR" />
            <Tab label={`Clientes (${roleCounts.CUSTOMER})`} value="CUSTOMER" />
            <Tab label={`Encargados (${roleCounts.PHARMACY_MANAGER})`} value="PHARMACY_MANAGER" />
            <Tab label={`Empleados (${roleCounts.PHARMACY_EMPLOYEE})`} value="PHARMACY_EMPLOYEE" />
          </Tabs>

          {users.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8, color: 'text.secondary' }}>
              <Person sx={{ fontSize: 48, opacity: 0.3, mb: 1 }} />
              <Typography>
                {search ? `Sin resultados para "${search}"` : 'No hay usuarios.'}
              </Typography>
              {!search && (
                <Button variant="contained" startIcon={<Add />} onClick={() => navigate('/admin/users/create')} sx={{ mt: 2 }}>
                  Crear primer usuario
                </Button>
              )}
            </Box>
          ) : isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              {users.map((user) => (
                <Card
                  key={user.id}
                  elevation={0}
                  onClick={() => {
                    if (isPendingOnboarding(user)) return;
                    navigate(`/admin/users/${user.id}`);
                  }}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    cursor: isPendingOnboarding(user) ? 'default' : 'pointer',
                    opacity: isPendingOnboarding(user) ? 0.85 : 1,
                    '&:hover': isPendingOnboarding(user) ? undefined : { borderColor: 'primary.main' },
                  }}
                >
                  <Box sx={{ p: 2 }}>
                    <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography sx={{ fontWeight: 600, color: NAVY, fontSize: 15 }} noWrap>
                          {getUserDisplayName(user)}
                        </Typography>
                        {hasFullDoctorName(user) ? (
                          <Typography sx={{ fontSize: 12, color: 'text.secondary' }} noWrap>
                            {user.email}
                          </Typography>
                        ) : getDoctorSecondaryLine(user) ? (
                          <Typography sx={{ fontSize: 12, color: 'text.secondary', fontStyle: 'italic' }} noWrap>
                            {getDoctorSecondaryLine(user)}
                          </Typography>
                        ) : null}
                      </Box>
                      <Chip label={roleLabels[user.role] || user.role} color={roleColors[user.role] || 'default'} size="small" sx={{ flexShrink: 0 }} />
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <Box sx={{ display: 'flex', gap: 0.75, alignItems: 'center', flexWrap: 'wrap' }}>
                        <StatusChip user={user} />
                        {user.role === 'DOCTOR' && (user.searchCount30d ?? 0) > 0 && (
                          <Chip
                            icon={<SearchIcon sx={{ fontSize: '12px !important' }} />}
                            label={`${user.searchCount30d}`}
                            size="small"
                            sx={{ height: 20, fontSize: 10, bgcolor: `${BLUE}12`, color: BLUE, '& .MuiChip-label': { px: 0.75 } }}
                          />
                        )}
                        {user.doctorProfile?.licenseNumber && (
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            Lic. {user.doctorProfile.licenseNumber}
                          </Typography>
                        )}
                        {user.doctorProfile?.specialtyName && (
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>
                            · {user.doctorProfile.specialtyName}
                          </Typography>
                        )}
                      </Box>
                      <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
                        {isPendingOnboarding(user) && (
                          <Tooltip title="Reenviar invitación">
                            <span>
                              <IconButton
                                size="small"
                                color="warning"
                                disabled={resendingUserId === user.id}
                                onClick={(e) => handleResendInvitation(e, user)}
                              >
                                {resendingUserId === user.id ? (
                                  <CircularProgress size={16} color="inherit" />
                                ) : (
                                  <Refresh fontSize="small" />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <IconButton
                          size="small"
                          color="error"
                          onClick={(e) => { e.stopPropagation(); handleDeleteClick(user); }}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Box>
          ) : (
            <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Nombre / Email</TableCell>
                      <TableCell>Rol</TableCell>
                      <TableCell>Especialidad</TableCell>
                      <TableCell>Detalles</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell>Búsq. 30d</TableCell>
                      <TableCell>Últ. actividad</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => {
                      const pending = isPendingOnboarding(user);
                      return (
                      <TableRow
                        key={user.id}
                        hover={!pending}
                        onClick={() => {
                          if (pending) return;
                          navigate(`/admin/users/${user.id}`);
                        }}
                        sx={{
                          cursor: pending ? 'default' : 'pointer',
                          opacity: pending ? 0.75 : 1,
                        }}
                      >
                        <TableCell>
                          <Typography variant="body2" fontWeight="medium">{getUserDisplayName(user)}</Typography>
                          {hasFullDoctorName(user) ? (
                            <Typography variant="caption" color="text.secondary">{user.email}</Typography>
                          ) : getDoctorSecondaryLine(user) ? (
                            <Typography variant="caption" color="text.secondary" fontStyle="italic">
                              {getDoctorSecondaryLine(user)}
                            </Typography>
                          ) : null}
                        </TableCell>
                        <TableCell>
                          <Chip label={roleLabels[user.role] || user.role} color={roleColors[user.role] || 'default'} size="small" />
                        </TableCell>
                        <TableCell>
                          {user.doctorProfile?.specialtyName ? (
                            <Typography variant="body2">{user.doctorProfile.specialtyName}</Typography>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.doctorProfile?.licenseNumber && (
                            <Typography variant="body2" color="text.secondary">Licencia: {user.doctorProfile.licenseNumber}</Typography>
                          )}                          {!user.doctorProfile?.licenseNumber && (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.25 }}>
                            <StatusChip user={user} />
                            {user.status === 'INVITED' && getStatusMeta(user).hint && (
                              <Typography variant="caption" color="text.disabled">
                                {getStatusMeta(user).hint}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          {user.role === 'DOCTOR' && (user.searchCount30d ?? 0) > 0 ? (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                              <SearchIcon sx={{ fontSize: 14, color: BLUE }} />
                              <Typography variant="body2" fontWeight={600} color={BLUE}>{user.searchCount30d}</Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          {user.lastActive ? (
                            <Box>
                              <Chip
                                label={getActivityStatus(user.lastActive).label}
                                color={getActivityStatus(user.lastActive).color}
                                size="small"
                                sx={{ mb: 0.25 }}
                              />
                              <Typography variant="caption" display="block" color="text.secondary">
                                {new Date(user.lastActive).toLocaleDateString()}
                              </Typography>
                            </Box>
                          ) : (
                            <Typography variant="body2" color="text.disabled">—</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {isPendingOnboarding(user) && (
                            <Tooltip title="Reenviar invitación">
                              <span>
                                <IconButton
                                  size="small"
                                  color="warning"
                                  disabled={resendingUserId === user.id}
                                  onClick={(e) => handleResendInvitation(e, user)}
                                >
                                  {resendingUserId === user.id ? (
                                    <CircularProgress size={18} color="inherit" />
                                  ) : (
                                    <Refresh />
                                  )}
                                </IconButton>
                              </span>
                            </Tooltip>
                          )}
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(e) => { e.stopPropagation(); handleDeleteClick(user); }}
                          >
                            <Delete />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
              />
            </Box>
          )}
        </>
      )}

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Eliminar usuario</DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas eliminar a <strong>{userToDelete ? getUserDisplayName(userToDelete) : ''}</strong>? Esta acción no se puede deshacer.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteUserMutation.isPending}
          >
            {deleteUserMutation.isPending ? 'Eliminando...' : 'Eliminar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!snackbar}
        autoHideDuration={4000}
        onClose={() => setSnackbar(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert onClose={() => setSnackbar(null)} severity={snackbar.severity} variant="filled" sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};
